import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Tooltip } from 'src/common';
import { useStateSelector } from 'src/redux';

const Div = styled.div<{ $slidesCount: number; $maxSteps: number }>`
  position: relative;
  height: 5px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 3px;

  .background {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${({ $slidesCount, $maxSteps }) => ($slidesCount / $maxSteps) * 100}%;
    background-color: ${({ theme }) => theme.colors.stroke};
    border-radius: 3px;
    z-index: 0;
  }

  svg {
    z-index: 2;
  }

  .line {
    border-radius: 3px;
    left: 0;
    position: absolute;
    height: 100%;
    background: ${({ theme }) => theme.colors.gradientVertical};
    z-index: 1;
  }

  .ref {
    position: absolute;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(${({ $maxSteps }) => $maxSteps}, 1fr);
    grid-gap: 4px;
    z-index: 3;

    .ref-btn {
      height: 12px;
      cursor: pointer;
    }

    .ref-btn.locked {
      cursor: default;
    }

    .ref-btn:nth-child(n + ${({ $slidesCount }) => $slidesCount + 1}) {
      display: none;
    }
  }
`;

interface StepperProps<T extends string> {
  slides: { title: string; id: T; locked?: boolean }[];
  step: number;
  onChangeSlide: (id: T) => void;
  maxSteps?: number;
}

export const Stepper = <T extends string>({ slides, step, onChangeSlide, maxSteps = 9 }: StepperProps<T>) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const total = slides.length;
  const stepSize = 100 / maxSteps;

  return (
    <Div $slidesCount={total} $maxSteps={maxSteps}>
      <div className="background" />

      {Array.from({ length: total - 1 }).map((_, idx) => (
        <svg
          key={idx}
          css="transform: translateX(0.5px)"
          style={{
            position: 'absolute',
            left: `${((idx + 1) / maxSteps) * 100}%`,
            transform: 'translateX(-50%)',
          }}
          width="8"
          height="5"
          viewBox="0 0 8 5"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 0H8C8 0 6.22222 0 6.22222 2.5C6.22222 5 8 5 8 5H0C0 5 1.77778 5 1.77778 2.5C1.77778 0 0 0 0 0Z"
            fill={colors.white}
          />
        </svg>
      ))}
      <div className="ref">
        {Array.from({ length: maxSteps }).map((_, idx) => {
          const slide = slides[idx];
          return slide ? (
            <Tooltip key={idx} dataHtml={slide.title} place="top" dataId="stepper-wiz">
              <div
                className={`ref-btn${slide.locked ? ' locked' : ''}`}
                onClick={() => !slide.locked && onChangeSlide(slide.id)}
              />
            </Tooltip>
          ) : (
            <div key={idx} className="ref-btn" style={{ display: 'none' }} />
          );
        })}
      </div>

      <motion.div className="line" initial={false} animate={{ width: `${stepSize * (step + 1)}%` }} />
    </Div>
  );
};
